import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { fetchGalaxyById, fetchGalaxyMembers, fetchGalaxyPosts } from '../lib/orbitApi';
import PhoneFrame from '../components/PhoneFrame';
import StarField from '../components/StarField';
import PostCard from '../components/PostCard';
import { GalaxyMark } from '../components/OrbitLogo';

// A single Galaxy's own page: its post feed plus a member list. Galaxy
// cards on the Home "Galaxies" tab link here instead of doing nothing.
export default function GalaxyDetail() {
  const nav = useNavigate();
  const { galaxyId } = useParams();
  const { authUser } = useApp() || {};
  const [galaxy, setGalaxy] = useState(null);
  const [members, setMembers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!authUser) return;
    setLoading(true);
    try {
      const [g, m, p] = await Promise.all([
        fetchGalaxyById(galaxyId),
        fetchGalaxyMembers(galaxyId),
        fetchGalaxyPosts([galaxyId], authUser.id),
      ]);
      setGalaxy(g);
      setMembers(m);
      setPosts(p);
    } catch (err) {
      toast(err.message || 'Could not load this Galaxy');
    } finally {
      setLoading(false);
    }
  }, [galaxyId, authUser]);

  useEffect(() => { load(); }, [load]);

  return (
    <PhoneFrame>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10">
          <div className="orbit-page-header">
            <button className="press orbit-icon-button" onClick={() => nav(-1)} aria-label="Go back">
              <ArrowLeft />
            </button>
            <div className="flex items-center justify-center gap-2 flex-1 min-w-0">
              <div className="w-8 h-8 rounded-full shrink-0" style={{ background: 'radial-gradient(circle at 40% 35%, #8b7ce8, transparent 75%)', boxShadow: '0 0 16px #8b7ce8' }} />
              <h1 className="font-display orbit-page-title capitalize truncate">
                {loading ? 'Loading…' : (galaxy?.name || 'Galaxy')}
              </h1>
            </div>
            <div className="w-10" aria-hidden="true" />
          </div>

          {!loading && members.length > 0 && (
            <div className="px-5 pb-3 flex gap-2 overflow-x-auto orbit-scroll">
              {members.map((m) => (
                <div key={m.id} className="flex flex-col items-center gap-1 shrink-0" style={{ width: 56 }}>
                  {m.avatar ? (
                    <img src={m.avatar} alt="" className="w-10 h-10 rounded-full object-cover" style={{ border: '1px solid rgba(160,140,230,.4)' }} />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)', color: '#fff' }}>
                      {(m.name || m.handle || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-xs truncate w-full text-center" style={{ color: '#9d92c4' }}>@{m.handle}</span>
                </div>
              ))}
            </div>
          )}

          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg,transparent,rgba(201,162,75,.5),transparent)' }} />

          <div>
            {loading && <p className="text-center py-8 italic" style={{ color: '#6f6795' }}>Loading…</p>}
            {!loading && posts.length === 0 && (
              <div className="flex flex-col items-center py-10">
                <div className="floaty mb-3"><GalaxyMark size={44} /></div>
                <p className="text-center italic" style={{ color: '#6f6795' }}>Nothing launched into this Galaxy yet.</p>
              </div>
            )}
            {posts.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
